import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { sseService } from 'src/common/services/sseService';
import { refreshSelectedSitesDetails } from 'src/common/config/FunctionHelper';
import { paths } from 'src/common/config/TextMessage';
import {
  setRefreshSiteId,
  setRefreshTable,
  setIndividualRefreshTable
} from 'src/redux/siteAuditReducer';
import { setKeywordTrackingDetails } from 'src/redux/keywordResearch';
import { setAiVisibilityRedisResponse } from 'src/redux/dashboard';
import { setIsLoginScan } from 'src/redux/counterSlice';

/** Routes on which the SSE connection should be kept alive. */
const SSE_ACTIVE_PATHS = new Set([
  paths.siteAudit,
  paths.settings,
  paths.billing,
  paths.externalScans,
  paths.userManagement,
  paths.seoTasks,
  paths.keywordResearch,
  paths.leadGeneration,
  paths.keywordOverview,
  paths.competitorAnalysis,
  paths.blogPostIdeas,
  paths.onBoarding,
  paths.statusCheck,
  paths.dashboard,
  paths.keywordTracking,
  paths.otherTools,       // '/local-seo'
  '/other-tools',         // legacy path — keep for backwards-compat
  '/other-tools/backlinks',
  '/other-tools/local-seo-tools',
  paths.keywordsLists,
  paths.backlinkPage,
  paths.localChecker,
  paths.googleBusiness,
  paths.mapRankTracker,
  paths.localTasks,
  paths.googleReviews,
  paths.businessPosts,
  paths.listingManager,
  paths.aiVisibility
]);

/** Paths where audit score / table refresh events should be applied. */
const SCORE_REFRESH_PATHS = new Set([
  paths.siteAudit,
  paths.scannedPages,
  paths.dashboard
]);

/**
 * Manages the Server-Sent Events (SSE) connection lifecycle:
 * - Subscribes when the user is on an SSE-active path with a valid siteId
 * - Handles `site-update`, `keyword-update`, and `ai-visibility-update` events
 * - Disconnects when navigating away from active paths
 * - Cleans up on unmount
 */
export const useSSEManager = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const siteId = useSelector((state: RootState) => state.siteAudit.siteId) ?? 0;
  const isLoginScan = useSelector((state: RootState) => state.counter.isCallLoginScan);
  const keywordTrackingSection =
    useSelector((state: RootState) => state.keywordResearch.keywordTrackingSection) ?? 0;
  const websiteMonitoringDetails: any =
    useSelector((state: RootState) => state.siteAudit.websiteMonitoringDetails) ?? {};

  const isScanInitialized = useRef(false);

  // ── SSE subscribe ────────────────────────────────────────────────────────
  const subscribe = useCallback(() => {
    dispatch(setIsLoginScan(false));

    sseService.subscribe('app', {
      siteId,
      eventHandlers: {
        'site-update': (updates: any) => {
          if (!Array.isArray(updates)) return;
          const currentPath = window.location.pathname;

          updates.forEach((eventData) => {
            if (SCORE_REFRESH_PATHS.has(currentPath)) {
              refreshSelectedSitesDetails(eventData);
            }

            if (eventData?.sites_audit === true && SCORE_REFRESH_PATHS.has(currentPath)) {
              dispatch(setRefreshSiteId(eventData._id));
              dispatch(setRefreshTable(true));
            } else if (
              eventData?.sites_audit === false &&
              (currentPath === paths.externalScans || SCORE_REFRESH_PATHS.has(currentPath))
            ) {
              dispatch(setIndividualRefreshTable(true));
              if (SCORE_REFRESH_PATHS.has(currentPath)) {
                refreshSelectedSitesDetails(eventData);
              }
            }
          });
        },

        'keyword-update': (updates: any) => {
          if (!Array.isArray(updates)) return;
          updates.forEach((eventData) => dispatch(setKeywordTrackingDetails(eventData)));
        },

        'ai-visibility-update': (updates: any) => {
          if (!Array.isArray(updates)) return;
          updates.forEach((eventData: any) => {
            if (eventData?.siteId && eventData.siteId === siteId) {
              dispatch(setAiVisibilityRedisResponse(eventData));
            }
          });
        }
      }
    });
  }, [dispatch, siteId]);

  const unsubscribe = () => sseService.unsubscribe('app');

  // ── Reset the one-shot guard when siteId changes so re-subscription
  //    happens for the new site (e.g. user adds a second site without
  //    a page refresh). Without this, `isScanInitialized` stays `true`
  //    forever and `subscribe()` is never called for the new siteId.
  useEffect(() => {
    isScanInitialized.current = false;
  }, [siteId]);

  // ── Subscribe when conditions are met ────────────────────────────────────
  useEffect(() => {
    const isKeywordProgress =
      (keywordTrackingSection === 3 ||
        websiteMonitoringDetails?.keywordProcessing?.isProcessing) &&
      pathname === paths.keywordTracking;

    if (SSE_ACTIVE_PATHS.has(pathname) && isKeywordProgress) {
      subscribe();
    } else if (
      (SSE_ACTIVE_PATHS.has(pathname) || isLoginScan) &&
      !isScanInitialized.current &&
      siteId
    ) {
      isScanInitialized.current = true;
      subscribe();
    }
  }, [siteId, pathname, isLoginScan, keywordTrackingSection, websiteMonitoringDetails, subscribe]);

  // ── Disconnect when leaving SSE-active pages ─────────────────────────────
  useEffect(() => {
    if (!SSE_ACTIVE_PATHS.has(pathname)) {
      unsubscribe();
    }
  }, [pathname]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => unsubscribe();
  }, []);
};
